import { ChevronsUpDown, LogOut, User } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { productRoles } from '../auth/rbac';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { cn } from './ui/utils';

interface AccountMenuProps {
  /** Compact icon-only trigger (sidebar rails) vs. a full name+role row (wide sidebars). */
  variant?: 'icon' | 'full';
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  className?: string;
}

/**
 * The one place a signed-in user can see who they are and sign out --
 * previously this only existed as DevAuthStatus, gated to dev Keycloak
 * password mode and rendered only inside the clinical workspace header, so a
 * production Keycloak-redirect session (or any admin-only account) had no
 * sign-out affordance anywhere in the app. Shared between SidebarRail and
 * AdminLayout so both panels show the same identity/sign-out UI.
 */
export function AccountMenu({ variant = 'icon', side = 'right', align = 'start', className }: AccountMenuProps) {
  const { displayName, roles, logout } = useAuth();
  const visibleRoles = productRoles(roles);
  const name = displayName || 'Signed in';
  const initial = name.trim().charAt(0).toUpperCase() || 'U';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === 'icon' ? (
          <button
            type="button"
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-xl border border-sidebar-border bg-sidebar-accent/60 text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/50',
              className,
            )}
            title="Account"
            aria-label="Account menu"
          >
            <User className="h-4 w-4" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </button>
        ) : (
          <button
            type="button"
            className={cn(
              'flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm font-medium text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-foreground focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/50',
              className,
            )}
            aria-label="Account menu"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-medium text-primary">
              {initial}
            </span>
            <span className="min-w-0 flex-1 truncate">{name}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/50" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent side={side} align={align} className="w-56">
        <DropdownMenuLabel className="truncate">{name}</DropdownMenuLabel>
        {visibleRoles.length > 0 && (
          // #107: the clinical header used to carry a second identity chip
          // (DevAuthStatus) that listed EVERY role, one per line. That chip is
          // gone as a duplicate of this menu, so this menu has to show what it
          // showed — the full list, not the first four joined by commas.
          // Scrollable, because an account can hold a dozen roles.
          <ul className="max-h-40 space-y-0.5 overflow-y-auto px-2 pb-1.5">
            {visibleRoles.map((role) => (
              <li key={role} className="text-xs text-text-tertiary">
                {role}
              </li>
            ))}
          </ul>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} variant="destructive">
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
